import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pwd = () => encodeURIComponent(sessionStorage.getItem('adminPassword') || '');

// Shows the ads.txt production monitor status at the top of Admin; red alert when failing.
export default function SiteHealthBanner() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/site-health?password=${pwd()}`);
      setStatus(res.data.status);
    } catch (e) {
      setStatus(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/admin/site-health/run?password=${pwd()}`);
      await load();
    } finally {
      setRunning(false);
    }
  };

  if (!status) return null;
  const failing = !status.ok;
  const problems = (status.last_result || []).flatMap(r => (r.problems || []).map(p => `${r.url}: ${p}`));

  return (
    <div
      className={`mb-6 rounded-lg border p-4 flex items-start gap-3 ${failing ? 'bg-red-50 border-red-400 text-red-900' : 'bg-green-50 border-green-300 text-green-900'}`}
      data-testid="site-health-banner"
    >
      {failing ? <AlertTriangle className="h-5 w-5 mt-0.5" /> : <CheckCircle2 className="h-5 w-5 mt-0.5" />}
      <div className="flex-1 text-sm">
        <div className="font-bold" data-testid="site-health-status">
          {failing ? 'ALERT: ads.txt is failing on shelovesthee.com — AdSense revenue at risk' : 'ads.txt monitor: OK'}
        </div>
        <div>Last checked {new Date(status.last_checked_at).toLocaleString()}{status.failing_since && ` • failing since ${new Date(status.failing_since).toLocaleString()}`}</div>
        {failing && <ul className="list-disc ml-5 mt-1">{problems.map(p => <li key={p}>{p}</li>)}</ul>}
        {failing && (
          <div className="mt-2" data-testid="site-health-fix-steps">
            <strong>How to fix:</strong>
            <ol className="list-decimal ml-5">
              <li>Wait 5 minutes and click <em>Check now</em> — right after a deploy the hosting edge can briefly serve the app page instead of ads.txt.</li>
              <li>If it still fails, redeploy the app (ads.txt is in the project and included in every build).</li>
              <li>If it still fails after redeploying, contact Emergent support: the hosting edge is not serving frontend/public/ads.txt.</li>
              <li>Once it passes, open AdSense → Sites → shelovesthee.com and request an ads.txt re-check.</li>
            </ol>
          </div>
        )}
        {!failing && status.source && <div className="text-xs opacity-75">Source: {status.source}</div>}
      </div>
      <Button size="sm" variant="outline" onClick={runNow} disabled={running} data-testid="site-health-run-btn">
        <RefreshCw className={`h-4 w-4 mr-1 ${running ? 'animate-spin' : ''}`} /> Check now
      </Button>
    </div>
  );
}
